import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { FileText, Activity, Wrench, AlertTriangle, Star, Send, Settings, DollarSign, Sparkles, BookOpen, GitBranch, Mail } from 'lucide-react';
import {
  adminApi,
  type AdminLetterOutDetail,
  type LetterRating,
  type LetterRatingInput,
  type LetterAutoReview,
} from '../../lib/api';
import {
  MoodRadar,
  LetterHeader,
  LetterInspector,
  TokenUsageChart,
  CostBreakdown,
  PipelineTimeline,
  RepairViolationsPanel,
  RatingPanel,
  AutoReviewPanel,
  DeliveryControls,
  InboundLetterCard,
  type PipelineStageData,
} from '../../components/admin/letter-review';

type DetailTab =
  | 'letter'
  | 'inbound'
  | 'pipeline'
  | 'cost'
  | 'rating'
  | 'review'
  | 'tokens'
  | 'plan'
  | 'mood'
  | 'delivery'
  | 'models'
  | 'debug';

const TAB_HASH: Record<DetailTab, string> = {
  letter: 'letter',
  inbound: 'inbound',
  pipeline: 'pipeline',
  cost: 'cost',
  rating: 'rating',
  review: 'ai-review',
  tokens: 'tokens',
  plan: 'plan',
  mood: 'mood',
  delivery: 'delivery',
  models: 'models',
  debug: 'debug',
};

const HASH_TO_TAB = Object.fromEntries(
  Object.entries(TAB_HASH).map(([tab, hash]) => [hash, tab]),
) as Record<string, DetailTab>;

export function OutboundMailDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [letter, setLetter] = useState<AdminLetterOutDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [letterView, setLetterView] = useState<'final' | 'draft' | 'polish'>('final');
  const [costDetail, setCostDetail] = useState<Awaited<
    ReturnType<typeof adminApi.getLetterCost>
  > | null>(null);
  const [inboundCostDetail, setInboundCostDetail] = useState<Awaited<
    ReturnType<typeof adminApi.getLetterCost>
  > | null>(null);
  const [costNotFound, setCostNotFound] = useState(false);
  const [isCostLoading, setIsCostLoading] = useState(false);
  const [rating, setRating] = useState<LetterRating | null>(null);
  const [isRatingLoading, setIsRatingLoading] = useState(false);
  const [autoReview, setAutoReview] = useState<LetterAutoReview | null>(null);
  const [isAutoReviewLoading, setIsAutoReviewLoading] = useState(false);
  const [isRequestingAutoReview, setIsRequestingAutoReview] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>('letter');
  const location = useLocation();
  const navigate = useNavigate();

  const availableTabs = useMemo(() => {
    const tabs: DetailTab[] = ['letter', 'rating', 'review'];
    if (letter?.answeredLetters.length) tabs.push('inbound');

    const extraction =
      letter?.extraction && typeof letter.extraction === 'object'
        ? (letter.extraction as Record<string, unknown>)
        : null;
    const generation =
      extraction?.generation && typeof extraction.generation === 'object'
        ? (extraction.generation as Record<string, unknown>)
        : null;

    if (generation) {
      tabs.push('pipeline', 'models');
      const planUsage =
        typeof generation.plan_usage === 'object'
          ? (generation.plan_usage as Record<string, unknown>)
          : null;
      const hasRepair =
        !!generation.plan_initial && !!generation.plan && !!(planUsage && 'repair' in planUsage);
      const hasPlanData = !!(
        generation.consistency ||
        generation.plan_check ||
        generation.plan_gate ||
        hasRepair
      );
      if (hasPlanData) tabs.push('plan');
      if (generation.plan && typeof generation.plan === 'object') {
        const planData = (generation.plan as Record<string, unknown>).data as
          | Record<string, unknown>
          | undefined;
        if (planData && typeof planData.mood === 'object') tabs.push('mood');
      }
    }

    if (costDetail || isCostLoading || costNotFound) tabs.push('cost');

    const costEvents = (costDetail as { events?: Array<unknown> } | null)?.events;
    const hasTokenData = !!(
      (costDetail?.stages && costDetail.stages.length > 0) ||
      (costEvents && costEvents.length > 0)
    );
    if (hasTokenData) tabs.push('tokens');

    if (letter && letter.status !== 'sent') tabs.push('delivery');
    if (extraction) tabs.push('debug');

    return new Set(tabs);
  }, [letter, costDetail, isCostLoading, costNotFound]);

  const fetchLetter = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminApi.getLetterOut(id);
      setLetter(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load letter');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchRating = useCallback(async () => {
    if (!id) return;
    setIsRatingLoading(true);
    try {
      const data = await adminApi.getLetterRating(id);
      setRating(data);
    } catch {
      // Rating not found is expected, don't show error
      setRating(null);
    } finally {
      setIsRatingLoading(false);
    }
  }, [id]);

  const fetchAutoReview = useCallback(async () => {
    if (!id) return;
    setIsAutoReviewLoading(true);
    try {
      const data = await adminApi.getLetterAutoReview(id);
      setAutoReview(data);
    } catch {
      setAutoReview(null);
    } finally {
      setIsAutoReviewLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLetter();
    fetchRating();
    fetchAutoReview();
  }, [fetchLetter, fetchRating, fetchAutoReview]);

  useEffect(() => {
    if (!letter) return;
    const rawHash = location.hash.replace('#', '');
    const requestedTab = HASH_TO_TAB[rawHash];
    if (requestedTab && availableTabs.has(requestedTab) && requestedTab !== activeTab) {
      setActiveTab(requestedTab);
    }
  }, [location.hash, availableTabs, activeTab, letter]);

  const selectTab = (tab: DetailTab) => {
    setActiveTab(tab);
    const hash = TAB_HASH[tab];
    if (location.hash !== `#${hash}`) {
      navigate({ pathname: location.pathname, hash: `#${hash}` }, { replace: true });
    }
  };

  useEffect(() => {
    const loadCost = async () => {
      if (!id) return;
      setIsCostLoading(true);
      setCostNotFound(false);
      try {
        const data = await adminApi.getLetterCost(id);
        setCostDetail(data);
      } catch (err) {
        // Check if it's a 404 (no cost data found)
        const errorMessage = err instanceof Error ? err.message : '';
        if (errorMessage.includes('No cost data') || errorMessage.includes('404')) {
          setCostNotFound(true);
        }
        setCostDetail(null);
      } finally {
        setIsCostLoading(false);
      }
    };
    loadCost();
  }, [id]);

  // Fetch inbound cost (for the primary answered letter)
  useEffect(() => {
    const loadInboundCost = async () => {
      const inboundId = letter?.answeredLetters?.[0]?.id;
      if (!inboundId) {
        setInboundCostDetail(null);
        return;
      }
      try {
        const data = await adminApi.getLetterCost(inboundId);
        // Skip if same record as outbound (same letterId means combined record)
        if (data.letterId === costDetail?.letterId) {
          setInboundCostDetail(null);
          return;
        }
        setInboundCostDetail(data);
      } catch {
        setInboundCostDetail(null);
      }
    };
    loadInboundCost();
  }, [letter?.answeredLetters, costDetail?.letterId]);

  const handleCopyText = async (text: string, label: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        alert(`${label} copied to clipboard.`);
        return;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (!success) {
        throw new Error('Clipboard copy blocked');
      }
      alert(`${label} copied to clipboard.`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to copy to clipboard');
    }
  };

  const handleDownloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleQueueNow = async (reason: string) => {
    if (!letter) return;
    await adminApi.queueLetterNow(letter.id, reason);
    alert('Letter queued for immediate send');
    await fetchLetter();
  };

  const handleResend = async (reason: string) => {
    if (!id) return;
    await adminApi.resend(id, reason);
    alert('Resend job queued successfully');
  };

  const handleSubmitRating = async (data: LetterRatingInput) => {
    if (!id) return;
    const newRating = await adminApi.submitLetterRating(id, data);
    setRating(newRating);
    alert('Rating saved successfully');
  };

  const handleDeleteRating = async () => {
    if (!id) return;
    await adminApi.deleteLetterRating(id);
    setRating(null);
    alert('Rating deleted');
  };

  const handleRequestAutoReview = async (force: boolean) => {
    if (!id) return;
    if (force && !window.confirm('Re-run AI review? This will overwrite the existing review.')) {
      return;
    }
    setIsRequestingAutoReview(true);
    try {
      const response = await adminApi.requestLetterAutoReview(id, force);
      alert(response.message);
      await fetchAutoReview();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to queue AI review');
    } finally {
      setIsRequestingAutoReview(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="text-mist animate-pulse">Loading letter...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-ember mb-4">{error}</div>
        <Link to="/admin/letters-out" className="text-rune hover:underline">
          Back to Outbound Mail
        </Link>
      </div>
    );
  }

  if (!letter) return null;

  // Extract metadata from letter
  const extractionMeta =
    letter.extraction && typeof letter.extraction === 'object'
      ? (letter.extraction as Record<string, unknown>)
      : null;
  const generationMeta =
    extractionMeta?.generation && typeof extractionMeta.generation === 'object'
      ? (extractionMeta.generation as Record<string, unknown>)
      : null;

  // Extract text versions
  const draftText =
    generationMeta && typeof generationMeta.draft_text === 'string'
      ? generationMeta.draft_text
      : null;
  const polishText =
    generationMeta && typeof generationMeta.polish_text === 'string'
      ? generationMeta.polish_text
      : null;
  const polishSkipped =
    generationMeta && typeof generationMeta.polish_skipped === 'boolean'
      ? generationMeta.polish_skipped
      : null;
  const polishModel =
    generationMeta && typeof generationMeta.polish_model === 'string'
      ? generationMeta.polish_model
      : null;

  // Extract generation metadata
  const generationUtilityModel =
    generationMeta && typeof generationMeta.utilityModel === 'string'
      ? generationMeta.utilityModel
      : null;
  const generationModel =
    generationMeta && typeof generationMeta.model === 'string'
      ? generationMeta.model
      : 'unknown';
  const tokensTotal =
    generationMeta && typeof generationMeta.tokensTotal === 'number'
      ? generationMeta.tokensTotal
      : 0;
  const durationTotalMs =
    generationMeta && typeof generationMeta.durationTotalMs === 'number'
      ? generationMeta.durationTotalMs
      : 0;

  // Plan data
  const planUsage =
    generationMeta && typeof generationMeta.plan_usage === 'object'
      ? (generationMeta.plan_usage as Record<string, unknown>)
      : null;
  const planUsageInitial =
    planUsage && 'initial' in planUsage
      ? (planUsage.initial as Record<string, unknown> | null)
      : planUsage;
  const planUsageRepair =
    planUsage && 'repair' in planUsage
      ? (planUsage.repair as Record<string, unknown> | null)
      : null;
  const planInitial =
    generationMeta && typeof generationMeta.plan_initial === 'object'
      ? (generationMeta.plan_initial as Record<string, unknown>)
      : null;
  const planFinal =
    generationMeta && typeof generationMeta.plan === 'object'
      ? (generationMeta.plan as Record<string, unknown>)
      : null;
  const planGateRaw =
    generationMeta && typeof generationMeta.plan_gate === 'object'
      ? (generationMeta.plan_gate as Record<string, unknown>)
      : null;
  const planGate =
    planGateRaw &&
    typeof planGateRaw.triggered === 'boolean' &&
    typeof planGateRaw.source === 'string' &&
    Array.isArray(planGateRaw.reasons)
      ? {
          triggered: planGateRaw.triggered,
          source: planGateRaw.source as 'heuristic' | 'check' | 'none',
          reasons: planGateRaw.reasons as string[],
        }
      : null;
  const planCheck =
    generationMeta && typeof generationMeta.plan_check === 'object'
      ? (generationMeta.plan_check as Record<string, unknown>)
      : null;
  const hasRepair = !!(planInitial && planFinal && planUsageRepair);

  // Usage data for each stage
  const letterUsage =
    generationMeta && typeof generationMeta.letter_usage === 'object'
      ? (generationMeta.letter_usage as Record<string, unknown>)
      : null;
  const consistencyUsage =
    generationMeta && typeof generationMeta.consistency_usage === 'object'
      ? (generationMeta.consistency_usage as Record<string, unknown>)
      : null;
  const polishUsage =
    generationMeta && typeof generationMeta.polish_usage === 'object'
      ? (generationMeta.polish_usage as Record<string, unknown>)
      : null;

  // World builder metadata
  const worldBuilderMeta =
    extractionMeta?.world_builder && typeof extractionMeta.world_builder === 'object'
      ? (extractionMeta.world_builder as Record<string, unknown>)
      : null;
  const worldDeepenMeta =
    extractionMeta?.world_deepen && typeof extractionMeta.world_deepen === 'object'
      ? (extractionMeta.world_deepen as Record<string, unknown>)
      : null;
  const connectionEnrichmentMeta =
    extractionMeta?.connection_enrichment && typeof extractionMeta.connection_enrichment === 'object'
      ? (extractionMeta.connection_enrichment as Record<string, unknown>)
      : null;
  const summariesMeta =
    extractionMeta?.summaries && typeof extractionMeta.summaries === 'object'
      ? (extractionMeta.summaries as Record<string, unknown>)
      : null;

  // Build pipeline stages
  const pipelineStages: PipelineStageData[] = [];

  // --- Inbound Letter Processing Stages (from answeredLetters) ---
  if (letter.answeredLetters && letter.answeredLetters.length > 0) {
    // Use the most recent answered letter for extraction metadata
    const primaryInbound = letter.answeredLetters[letter.answeredLetters.length - 1];
    const inboundExtraction = primaryInbound?.extraction as Record<string, unknown> | null;

    if (inboundExtraction) {
      // Extraction stage
      const extractionData = inboundExtraction.extraction as Record<string, unknown> | undefined;
      if (extractionData) {
        const extractUsage = extractionData.usage as Record<string, unknown> | undefined;
        pipelineStages.push({
          id: 'extraction',
          title: 'Extract World Update',
          description: 'Extract entities, events, and world changes from inbound letter',
          model: String(extractionData.model ?? 'unknown'),
          status: extractionData.status === 'success' ? 'success' : 'error',
          output: extractionData.delta,
          outputLabel: 'World Update Delta',
          metrics: {
            inputTokens: typeof extractUsage?.prompt_tokens === 'number' ? extractUsage.prompt_tokens : undefined,
            outputTokens: typeof extractUsage?.completion_tokens === 'number' ? extractUsage.completion_tokens : undefined,
          },
        });
      }

      // Correspondent Profile stage
      const profileData = inboundExtraction.correspondent_profile as Record<string, unknown> | undefined;
      if (profileData) {
        const profileUsage = profileData.usage as Record<string, unknown> | undefined;
        pipelineStages.push({
          id: 'correspondent-profile',
          title: 'Correspondent Profile',
          description: 'Update understanding of the correspondent based on their letter',
          model: String(profileData.model ?? 'unknown'),
          status: 'success',
          output: profileData.profile,
          outputLabel: 'Profile Update',
          metrics: {
            inputTokens: typeof profileUsage?.prompt_tokens === 'number' ? profileUsage.prompt_tokens : undefined,
            outputTokens: typeof profileUsage?.completion_tokens === 'number' ? profileUsage.completion_tokens : undefined,
          },
        });
      }

      // Inner Dialog stage (if present)
      const dialogData = inboundExtraction.correspondent_inner_dialog as Record<string, unknown> | undefined;
      if (dialogData) {
        const dialogUsage = dialogData.usage as Record<string, unknown> | undefined;
        pipelineStages.push({
          id: 'inner-dialog',
          title: 'Inner Dialog',
          description: 'Generate correspondent\'s internal thoughts and reactions',
          model: String(dialogData.model ?? 'unknown'),
          status: 'success',
          output: dialogData.dialog,
          outputLabel: 'Inner Dialog',
          metrics: {
            inputTokens: typeof dialogUsage?.prompt_tokens === 'number' ? dialogUsage.prompt_tokens : undefined,
            outputTokens: typeof dialogUsage?.completion_tokens === 'number' ? dialogUsage.completion_tokens : undefined,
          },
        });
      }

      // Connection Enrichment from inbound (if present)
      const inboundConnections = inboundExtraction.connection_enrichment as Record<string, unknown> | undefined;
      if (inboundConnections) {
        const connUsage = inboundConnections.usage as Record<string, unknown> | undefined;
        pipelineStages.push({
          id: 'inbound-connections',
          title: 'Connection Enrichment',
          description: 'Infer relationships and threads from the inbound letter',
          model: String(inboundConnections.model ?? 'unknown'),
          status: 'success',
          output: {
            relationshipsAdded: inboundConnections.relationshipsAdded,
            threadsAdded: inboundConnections.threadsAdded,
          },
          metrics: {
            inputTokens: typeof connUsage?.prompt_tokens === 'number' ? connUsage.prompt_tokens : undefined,
            outputTokens: typeof connUsage?.completion_tokens === 'number' ? connUsage.completion_tokens : undefined,
          },
        });
      }

      // Summaries from inbound extraction
      const inboundSummaries = inboundExtraction.summaries as Record<string, unknown> | undefined;
      if (inboundSummaries) {
        const sumUsage = inboundSummaries.usage as Record<string, unknown> | undefined;
        pipelineStages.push({
          id: 'inbound-summaries',
          title: 'Summaries (Extract)',
          description: 'Regenerate world briefs after extraction',
          model: String(inboundSummaries.model ?? 'unknown'),
          status: 'success',
          output: inboundSummaries,
          metrics: {
            inputTokens: typeof sumUsage?.prompt_tokens === 'number' ? sumUsage.prompt_tokens : undefined,
            outputTokens: typeof sumUsage?.completion_tokens === 'number' ? sumUsage.completion_tokens : undefined,
          },
        });
      }
    }
  }

  // --- Generation Stages (from letterOut) ---
  if (generationMeta) {
    // Planning Input
    pipelineStages.push({
      id: 'plan-input',
      title: 'Planning Input',
      description: 'Context and constraints sent to the daily planner',
      model: generationUtilityModel ?? 'unknown',
      status: 'success',
      input: generationMeta.plan_input,
      output: generationMeta.plan_input_compact,
      outputLabel: 'Compact Summary',
      promptStats: getPromptStats(planUsageInitial),
    });

    // Daily Plan stages
    if (hasRepair) {
      pipelineStages.push({
        id: 'plan-initial',
        title: 'Daily Plan (Initial)',
        description: 'First-pass planner output before repair',
        model: generationUtilityModel ?? 'unknown',
        status: 'success',
        output: planInitial,
        promptStats: getPromptStats(planUsageInitial),
      });
      pipelineStages.push({
        id: 'plan-repair',
        title: 'Daily Plan (Repaired)',
        description: 'Plan repaired to satisfy epistolary constraints',
        model: generationUtilityModel ?? 'unknown',
        status: 'success',
        output: planFinal,
        promptStats: getPromptStats(planUsageRepair),
      });
    } else {
      pipelineStages.push({
        id: 'plan',
        title: 'Daily Plan',
        description: 'Planner output used to guide letter generation',
        model: generationUtilityModel ?? 'unknown',
        status: 'success',
        output: generationMeta.plan,
        promptStats: getPromptStats(planUsageInitial),
      });
    }

    // Letter Draft
    pipelineStages.push({
      id: 'letter-draft',
      title: 'Letter Draft',
      description: 'Generated letter content',
      model: generationModel,
      status: 'success',
      output: draftText ?? letter.bodyText,
      outputLabel: 'Body Text',
      outputIsJson: false,
      promptStats: getPromptStats(letterUsage),
    });

    // Letter Polish
    if (polishText || polishUsage || polishSkipped) {
      pipelineStages.push({
        id: 'letter-polish',
        title: 'Letter Polish',
        description: polishSkipped ? 'Polish stage was skipped' : 'Polished the draft for clarity and flow',
        model: polishModel ?? generationModel,
        status: polishSkipped ? 'skipped' : 'success',
        input: draftText ?? letter.bodyText,
        output: polishSkipped ? { skipped: true } : polishText,
        outputLabel: 'Polished Text',
        outputIsJson: polishSkipped ?? false,
        promptStats: getPromptStats(polishUsage),
      });
    }

    // Consistency Check
    pipelineStages.push({
      id: 'consistency',
      title: 'Consistency Check',
      description: 'Validation pass on the draft letter',
      model: generationUtilityModel ?? 'unknown',
      status: 'success',
      output: generationMeta.consistency,
      promptStats: getPromptStats(consistencyUsage),
    });
  }

  // World evolution stages
  if (worldBuilderMeta) {
    const wbUsage = worldBuilderMeta.usage as Record<string, unknown> | undefined;
    pipelineStages.push({
      id: 'world-builder',
      title: 'World Builder',
      description: 'World updates derived from the letter',
      model: String(worldBuilderMeta.model ?? 'unknown'),
      status: 'success',
      output: worldBuilderMeta.delta,
      metrics: {
        inputTokens: typeof wbUsage?.prompt_tokens === 'number' ? wbUsage.prompt_tokens : undefined,
        outputTokens: typeof wbUsage?.completion_tokens === 'number' ? wbUsage.completion_tokens : undefined,
        durationMs: typeof worldBuilderMeta.durationMs === 'number' ? worldBuilderMeta.durationMs : undefined,
      },
    });
  }

  if (connectionEnrichmentMeta) {
    pipelineStages.push({
      id: 'connection-enrichment',
      title: 'Connection Enrichment',
      description: 'Relationships or threads inferred from the update',
      model: String(connectionEnrichmentMeta.model ?? 'unknown'),
      status: 'success',
      output: connectionEnrichmentMeta,
    });
  }

  if (worldDeepenMeta) {
    pipelineStages.push({
      id: 'world-deepen',
      title: 'World Deepen',
      description: 'Additional world texture layered in after evolution',
      model: String(worldDeepenMeta.model ?? 'unknown'),
      status: 'success',
      output: worldDeepenMeta.delta,
    });
  }

  if (summariesMeta) {
    pipelineStages.push({
      id: 'summaries',
      title: 'Summaries',
      description: 'World briefs regenerated from latest canon',
      model: String(summariesMeta.model ?? 'unknown'),
      status: 'success',
      output: summariesMeta,
    });
  }

  // Mood data
  const planData = planFinal?.data as Record<string, unknown> | undefined;
  const mood = planData?.mood as Record<string, number> | undefined;
  const moodRationale = planData?.mood_rationale as string | undefined;

  // Consistency result
  const consistencyResult =
    generationMeta?.consistency && typeof generationMeta.consistency === 'object'
      ? (generationMeta.consistency as {
          passed?: boolean;
          violations?: Array<{ type: string; severity: 'error' | 'warning'; message: string; location?: string }>;
        })
      : null;
  const mediumConsistencyWarnings =
    generationMeta?.consistency && typeof generationMeta.consistency === 'object'
      ? ((generationMeta.consistency as {
          medium_violations?: Array<{
            type: string;
            severity: 'low' | 'medium' | 'high' | 'critical';
            message: string;
            fix?: string;
            fix_hint?: string;
          }>;
        }).medium_violations ?? null)
      : null;

  // Repair info
  const repairInfo = hasRepair
    ? {
        wasRepaired: true,
        originalPlan: planInitial,
        repairedPlan: planFinal,
        repairReason: 'Plan violated epistolary constraints and required repair',
      }
    : null;

  // Helper to extract cost stages from API response
  const extractStages = (detail: typeof costDetail) => {
    if (!detail) return [];
    if (detail.stages && detail.stages.length > 0) {
      return detail.stages.map((s) => ({
        stage: s.stage,
        cost: s.cost,
        costFormatted: `$${s.cost.toFixed(4)}`,
        model: s.model,
        inputTokens: s.inputTokens,
        outputTokens: s.outputTokens,
        latencyMs: s.latencyMs,
      }));
    }
    const events = (detail as { events?: Array<{ feature: string; cost: number; model: string; inputTokens: number; outputTokens: number; latencyMs: number }> })?.events;
    if (events && events.length > 0) {
      return events.map((e) => ({
        stage: e.feature,
        cost: e.cost,
        costFormatted: `$${e.cost.toFixed(4)}`,
        model: e.model,
        inputTokens: e.inputTokens,
        outputTokens: e.outputTokens,
        latencyMs: e.latencyMs,
      }));
    }
    return [];
  };

  const costStages = extractStages(costDetail);
  const inboundStages = extractStages(inboundCostDetail);

  // Combined end-to-end cost
  const hasInboundCost = inboundCostDetail !== null;
  const endToEndCost = (costDetail?.totalCost ?? 0) + (inboundCostDetail?.totalCost ?? 0);
  const endToEndCostFormatted = endToEndCost >= 1 ? `$${endToEndCost.toFixed(2)}` : `$${endToEndCost.toFixed(4)}`;

  // Token usage data for chart
  const tokenData = (() => {
    if (costDetail?.stages && costDetail.stages.length > 0) {
      return costDetail.stages.map((s) => ({
        stage: s.stage,
        inputTokens: s.inputTokens,
        outputTokens: s.outputTokens,
      }));
    }
    const events = (costDetail as { events?: Array<{ feature: string; inputTokens: number; outputTokens: number }> })?.events;
    if (events && events.length > 0) {
      return events.map((e) => ({
        stage: e.feature,
        inputTokens: e.inputTokens,
        outputTokens: e.outputTokens,
      }));
    }
    return [];
  })();

  const totalInputTokens = tokenData.reduce((sum, s) => sum + s.inputTokens, 0);
  const totalOutputTokens = tokenData.reduce((sum, s) => sum + s.outputTokens, 0);

  // Build debug bundle
  const resolvedLetterText =
    letterView === 'draft'
      ? draftText ?? letter.bodyText
      : letterView === 'polish'
        ? polishText ?? letter.bodyText
        : letter.bodyText;

  const debugBundle = {
    letter: {
      id: letter.id,
      worldId: letter.worldId,
      worldAddress: letter.worldAddress,
      status: letter.status,
      type: letter.type,
      subject: letter.subject,
      bodyText: letter.bodyText,
      errorMessage: letter.errorMessage ?? null,
      scheduledFor: letter.scheduledFor,
      createdAt: letter.createdAt,
      sentAt: letter.sentAt ?? null,
      retryCount: letter.retryCount,
      isQa: letter.isQa,
      user: letter.user,
    },
    extraction: extractionMeta,
    cost: costDetail,
    rating,
    autoReview,
  };
  const debugBundleText = JSON.stringify(debugBundle, null, 2);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="text-sm text-mist">
        <Link to="/admin/letters-out" className="hover:text-parchment">
          Outbound Mail
        </Link>
        <span className="mx-2">/</span>
        <span className="text-parchment font-mono">{letter.id}</span>
      </div>

      {/* Header */}
      <LetterHeader
        letter={letter}
        rating={rating}
        autoReview={autoReview}
        onCopyLetter={() => handleCopyText(resolvedLetterText, 'Letter')}
        onCopyDump={() => handleCopyText(debugBundleText, 'Full dump')}
        onDownloadDump={() => handleDownloadText(debugBundleText, `letter-${letter.id}-dump.json`)}
      />

      {/* Error Message (always visible if present) */}
      {letter.errorMessage && (
        <div className="bg-night border border-ember/40 rounded-xl p-4">
          <div className="text-ember text-sm font-medium mb-1 flex items-center gap-2">
            <AlertTriangle size={16} />
            Error Message
          </div>
          <div className="text-ember/80 text-sm font-mono">{letter.errorMessage}</div>
        </div>
      )}

      {/* Unified Tabbed Interface */}
      <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
        {/* Tab Navigation */}
        <div className="flex flex-wrap border-b border-stone/30 bg-void/30">
          <TabButton
            active={activeTab === 'letter'}
            onClick={() => selectTab('letter')}
            icon={<BookOpen size={14} />}
            label="Letter"
          />
          {letter.answeredLetters.length > 0 && (
            <TabButton
              active={activeTab === 'inbound'}
              onClick={() => selectTab('inbound')}
              icon={<Mail size={14} />}
              label="Inbound"
              badge={`${letter.answeredLetters.length}`}
            />
          )}
          {generationMeta && (
            <TabButton
              active={activeTab === 'pipeline'}
              onClick={() => selectTab('pipeline')}
              icon={<GitBranch size={14} />}
              label="Pipeline"
              badge={`${pipelineStages.length}`}
            />
          )}
          {(costDetail || isCostLoading || costNotFound) && (
            <TabButton
              active={activeTab === 'cost'}
              onClick={() => selectTab('cost')}
              icon={<DollarSign size={14} />}
              label="Cost"
              badge={endToEndCost > 0 ? endToEndCostFormatted : undefined}
            />
          )}
          <TabButton
            active={activeTab === 'rating'}
            onClick={() => selectTab('rating')}
            icon={<Star size={14} />}
            label="Rating"
            badge={rating ? `${rating.rating}/5` : undefined}
          />
          <TabButton
            active={activeTab === 'review'}
            onClick={() => selectTab('review')}
            icon={<Sparkles size={14} />}
            label="AI Review"
            badge={autoReview ? `${autoReview.rating}/10` : undefined}
          />
          {tokenData.length > 0 && (
            <TabButton
              active={activeTab === 'tokens'}
              onClick={() => selectTab('tokens')}
              icon={<Activity size={14} />}
              label="Tokens"
              badge={`${((totalInputTokens + totalOutputTokens) / 1000).toFixed(1)}k`}
            />
          )}
          {(repairInfo || consistencyResult) && (
            <TabButton
              active={activeTab === 'plan'}
              onClick={() => selectTab('plan')}
              icon={<Wrench size={14} />}
              label="Plan"
              badge={hasRepair ? 'Repaired' : undefined}
              badgeColor={hasRepair ? 'amber' : undefined}
            />
          )}
          {mood && (
            <TabButton
              active={activeTab === 'mood'}
              onClick={() => selectTab('mood')}
              icon={<Sparkles size={14} />}
              label="Mood"
            />
          )}
          {letter.status !== 'sent' && (
            <TabButton
              active={activeTab === 'delivery'}
              onClick={() => selectTab('delivery')}
              icon={<Send size={14} />}
              label="Delivery"
            />
          )}
          {generationMeta && (
            <TabButton
              active={activeTab === 'models'}
              onClick={() => selectTab('models')}
              icon={<Settings size={14} />}
              label="Models"
            />
          )}
          {extractionMeta && (
            <TabButton
              active={activeTab === 'debug'}
              onClick={() => selectTab('debug')}
              icon={<FileText size={14} />}
              label="Debug"
            />
          )}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'letter' && (
            <LetterInspector
              bodyText={letter.bodyText}
              draftText={draftText}
              polishText={polishText}
              polishSkipped={polishSkipped}
              letterView={letterView}
              onViewChange={setLetterView}
              onCopyText={handleCopyText}
              subject={letter.subject}
            />
          )}

          {activeTab === 'inbound' && letter.answeredLetters.length > 0 && (
            <div className="space-y-6">
              {letter.answeredLetters.map((inLetter) => (
                <InboundLetterCard
                  key={inLetter.id}
                  letter={inLetter}
                  onCopyText={handleCopyText}
                />
              ))}
            </div>
          )}

          {activeTab === 'pipeline' && generationMeta && (
            <PipelineTimeline
              stages={pipelineStages}
              hasRepair={hasRepair}
              totalDurationMs={durationTotalMs}
              totalTokens={tokensTotal}
              model={generationModel}
              utilityModel={generationUtilityModel}
            />
          )}

          {activeTab === 'cost' && (
            <div className="space-y-6">
              {/* End-to-end summary when we have both inbound and outbound costs */}
              {hasInboundCost && costDetail && (
                <div className="bg-rune/10 border border-rune/30 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-lg text-parchment">End-to-End Cost</h3>
                    <span className="text-2xl font-display text-rune">{endToEndCostFormatted}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-mist">Inbound Processing</span>
                      <span className="text-parchment ml-2">{inboundCostDetail?.totalCostFormatted}</span>
                    </div>
                    <div>
                      <span className="text-mist">Outbound Generation</span>
                      <span className="text-parchment ml-2">{costDetail.totalCostFormatted}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Outbound cost breakdown */}
              {costDetail && (
                <div>
                  {hasInboundCost && <h4 className="text-sm font-medium text-mist mb-3">Outbound Generation</h4>}
                  <CostBreakdown
                    totalCost={costDetail.totalCost}
                    totalCostFormatted={costDetail.totalCostFormatted}
                    totalLatencyMs={costDetail.totalLatencyMs ?? 0}
                    stages={costStages}
                  />
                </div>
              )}

              {/* Inbound cost breakdown */}
              {hasInboundCost && inboundCostDetail && (
                <div>
                  <h4 className="text-sm font-medium text-mist mb-3">Inbound Processing</h4>
                  <CostBreakdown
                    totalCost={inboundCostDetail.totalCost}
                    totalCostFormatted={inboundCostDetail.totalCostFormatted}
                    totalLatencyMs={inboundCostDetail.totalLatencyMs ?? 0}
                    stages={inboundStages}
                  />
                </div>
              )}

              {isCostLoading && (
                <div className="text-sm text-mist animate-pulse">Loading cost data...</div>
              )}
              {costNotFound && !isCostLoading && !costDetail && (
                <div>
                  <div className="flex items-center gap-2 text-sm text-mist">
                    <DollarSign size={16} className="text-mist/50" />
                    <span>No cost data recorded for this letter</span>
                  </div>
                  <p className="text-xs text-mist/60 mt-2">
                    Cost tracking may not have been enabled when this letter was generated.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'rating' && (
            <div className="space-y-6">
              <RatingPanel
                existingRating={rating}
                isLoading={isRatingLoading}
                onSubmit={handleSubmitRating}
                onDelete={handleDeleteRating}
              />
            </div>
          )}

          {activeTab === 'review' && (
            <div id="ai-review">
              <AutoReviewPanel
                review={autoReview}
                isLoading={isAutoReviewLoading}
                isRequesting={isRequestingAutoReview}
                onRequestReview={handleRequestAutoReview}
              />
            </div>
          )}

          {activeTab === 'tokens' && tokenData.length > 0 && (
            <TokenUsageChart
              data={tokenData}
              totalInputTokens={totalInputTokens}
              totalOutputTokens={totalOutputTokens}
            />
          )}

          {activeTab === 'plan' && (repairInfo || consistencyResult || planCheck || planGate) && (
            <RepairViolationsPanel
              repairInfo={repairInfo}
              consistencyResult={
                consistencyResult
                  ? {
                      passed: consistencyResult.passed ?? true,
                      violations: consistencyResult.violations ?? [],
                    }
                  : null
              }
              planCheck={planCheck}
              planGate={planGate}
              mediumWarnings={mediumConsistencyWarnings}
            />
          )}

          {activeTab === 'mood' && mood && (
            <MoodRadar mood={mood} rationale={moodRationale} />
          )}

          {activeTab === 'delivery' && letter.status !== 'sent' && (
            <DeliveryControls
              letterStatus={letter.status}
              onQueueNow={handleQueueNow}
              onResend={handleResend}
            />
          )}

          {activeTab === 'models' && generationMeta && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-mist mb-1">Primary Model</div>
                <div className="text-parchment font-mono text-xs">{generationModel}</div>
              </div>
              <div>
                <div className="text-mist mb-1">Utility Model</div>
                <div className="text-parchment font-mono text-xs">
                  {generationUtilityModel ?? '-'}
                </div>
              </div>
              {polishModel && (
                <div>
                  <div className="text-mist mb-1">Polish Model</div>
                  <div className="text-parchment font-mono text-xs">{polishModel}</div>
                </div>
              )}
              {worldBuilderMeta?.model != null && (
                <div>
                  <div className="text-mist mb-1">World Builder Model</div>
                  <div className="text-parchment font-mono text-xs">
                    {String(worldBuilderMeta.model)}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'debug' && extractionMeta && (
            <pre className="p-3 bg-void rounded-lg text-xs text-mist overflow-auto max-h-[500px]">
              {JSON.stringify(extractionMeta, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function to extract prompt stats
function getPromptStats(
  usage: Record<string, unknown> | null,
): { promptTokensEstimate?: number; promptContextLimit?: number; promptContextRatio?: number } | null {
  if (!usage) return null;
  const promptTokensEstimate = usage.promptTokensEstimate;
  const promptContextLimit = usage.promptContextLimit;
  const promptContextRatio = usage.promptContextRatio;
  if (
    promptTokensEstimate === undefined &&
    promptContextLimit === undefined &&
    promptContextRatio === undefined
  ) {
    return null;
  }
  return {
    promptTokensEstimate: typeof promptTokensEstimate === 'number' ? promptTokensEstimate : undefined,
    promptContextLimit: typeof promptContextLimit === 'number' ? promptContextLimit : undefined,
    promptContextRatio: typeof promptContextRatio === 'number' ? promptContextRatio : undefined,
  };
}

// Tab button component
function TabButton({
  active,
  onClick,
  icon,
  label,
  badge,
  badgeColor = 'rune',
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: string;
  badgeColor?: 'rune' | 'amber' | 'ember';
}) {
  const badgeColors = {
    rune: 'text-rune bg-rune/10',
    amber: 'text-amber-400 bg-amber-400/10',
    ember: 'text-ember bg-ember/10',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
        active
          ? 'text-parchment border-rune bg-night/50'
          : 'text-mist hover:text-parchment border-transparent hover:bg-night/30'
      }`}
    >
      {icon}
      <span>{label}</span>
      {badge && (
        <span className={`text-xs px-1.5 py-0.5 rounded ${badgeColors[badgeColor]}`}>
          {badge}
        </span>
      )}
    </button>
  );
}
